import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Music, Star, AlertCircle, ChevronRight, Lightbulb } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EnhancedCategoryCard } from '@/components/categories/EnhancedCategoryCard';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  software_type: string;
  holiday_type: string;
  featured: boolean;
  image_url: string | null;
  image_alt: string | null;
  song_count?: number;
}

export default function Categories() {
  // Fetch all categories with song counts
  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          *,
          category_songs (count)
        `)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      
      return data.map(category => ({
        ...category,
        song_count: category.category_songs?.[0]?.count || 0
      })) as Category[];
    },
  });

  // Group categories by software type
  const xLightsCategories = categories.filter(cat => cat.software_type === 'xLights');
  const lorCategories = categories.filter(cat => cat.software_type === 'Light-O-Rama');
  const bothCategories = categories.filter(cat => cat.software_type === 'Both');

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col">
        <NavBar />
        <main className="flex-1">
          <div className="container mx-auto px-4 py-8 pt-24">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load categories. Please try again later.
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Browse Categories | Light Hunt</title>
        <meta name="description" content="Browse light show sequence categories by software platform. Find Christmas, Halloween, and holiday sequences for xLights and Light-O-Rama." />
        <meta property="og:title" content="Browse Categories | Light Hunt" />
        <meta property="og:description" content="Browse light show sequence categories by software platform." />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-white">
        <NavBar />
        <main className="container mx-auto px-4 py-8 pt-24">
          {/* Hero Section */}
          <div className="mb-12 relative">
            <div className="absolute inset-0 bg-linear-to-r from-red-600/20 to-green-600/20 rounded-lg"></div>
            <div className="relative z-10 p-6">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Browse Categories
              </h1>
              <p className="text-lg text-gray-300 max-w-3xl">
                Discover curated collections of light show sequences organized by software platform and holiday themes.
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="h-8 w-48 bg-gray-700" />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(3)].map((_, j) => (
                      <Skeleton key={j} className="h-48 rounded-lg bg-gray-700" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* xLights Categories */}
              {xLightsCategories.length > 0 && (
                <section className="mb-16">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-1">xLights Sequences</h2>
                      <div className="inline-block bg-blue-900 text-blue-200 text-xs px-2 py-1 rounded">
                        Free & Open Source
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-400 mb-6">
                    Professional-quality sequences for the xLights lighting software platform
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {xLightsCategories.map((category) => (
                      <EnhancedCategoryCard
                        key={category.id}
                        id={category.id}
                        slug={category.slug}
                        title={category.name}
                        description={category.description}
                        platform={category.software_type}
                        sequenceCount={category.song_count || 0}
                        isFeatured={category.featured}
                        imageUrl={category.image_url}
                        imageAlt={category.image_alt}
                        gradientFrom="from-red-900/30"
                        gradientTo="to-green-900/30"
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Light-O-Rama Categories */}
              {lorCategories.length > 0 && (
                <section className="mb-16">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-1">Light-O-Rama Sequences</h2>
                      <div className="inline-block bg-blue-900 text-blue-200 text-xs px-2 py-1 rounded">
                        Traditional & Pixels
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-400 mb-6">
                    Sequences designed for Light-O-Rama controllers and software
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {lorCategories.map((category) => (
                      <EnhancedCategoryCard
                        key={category.id}
                        id={category.id}
                        slug={category.slug}
                        title={category.name}
                        description={category.description}
                        platform={category.software_type}
                        sequenceCount={category.song_count || 0}
                        isFeatured={category.featured}
                        imageUrl={category.image_url}
                        imageAlt={category.image_alt}
                        gradientFrom="from-amber-900/30"
                        gradientTo="to-blue-900/30"
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Universal Categories */}
              {bothCategories.length > 0 && (
                <section className="mb-16">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-1">Universal Collections</h2>
                      <div className="inline-block bg-blue-900 text-blue-200 text-xs px-2 py-1 rounded">
                        All Platforms
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-400 mb-6">
                    Curated collections available for both software platforms
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {bothCategories.map((category) => (
                      <EnhancedCategoryCard
                        key={category.id}
                        id={category.id}
                        slug={category.slug}
                        title={category.name}
                        description={category.description}
                        platform={category.software_type}
                        sequenceCount={category.song_count || 0}
                        isFeatured={category.featured}
                        imageUrl={category.image_url}
                        imageAlt={category.image_alt}
                        gradientFrom="from-blue-900/30"
                        gradientTo="to-purple-900/30"
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Featured Sequences Section */}
              <section className="mb-16">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Featured Sequences</h2>
                  <Link
                    to="/sequences"
                    className="text-blue-400 hover:text-blue-300 transition-colors flex items-center text-sm"
                  >
                    View all featured
                    <ChevronRight size={16} className="ml-1" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-all hover:border-gray-600">
                    <div className="relative h-48">
                      <img
                        src="https://images.unsplash.com/photo-1607262807149-dfd4c39320a6?q=80&w=2942&auto=format&fit=crop"
                        alt="Carol of the Bells"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-linear-to-t from-gray-900/80 to-transparent"></div>
                      <div className="absolute top-3 right-3 bg-blue-600 text-xs rounded-full px-2 py-1 flex items-center">
                        <Star size={12} className="mr-1" />
                        Featured
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-xl font-bold text-white">
                          Carol of the Bells - TSO
                        </h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-gray-300 mb-4">
                        Our most popular Christmas sequence featuring the iconic Trans-Siberian Orchestra arrangement.
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-400">
                          <Music size={16} className="mr-2" />
                          <span>1 sequence</span>
                        </div>
                        <Badge variant="outline" className="text-xs bg-blue-900/50 text-blue-200 border-gray-600">
                          xLights
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-all hover:border-gray-600">
                    <div className="relative h-48">
                      <img
                        src="https://images.unsplash.com/photo-1508361001413-7a9dca21d08a?q=80&w=2940&auto=format&fit=crop"
                        alt="This is Halloween"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-linear-to-t from-gray-900/80 to-transparent"></div>
                      <div className="absolute top-3 right-3 bg-blue-600 text-xs rounded-full px-2 py-1 flex items-center">
                        <Star size={12} className="mr-1" />
                        Featured
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <h3 className="text-xl font-bold text-white">
                          This is Halloween
                        </h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-gray-300 mb-4">
                        A spooky favorite that works for both Halloween and Christmas displays.
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-400">
                          <Music size={16} className="mr-2" />
                          <span>1 sequence</span>
                        </div>
                        <Badge variant="outline" className="text-xs bg-gray-700 text-gray-300 border-gray-600">
                          Both
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Call to Action */}
              <div className="bg-linear-to-r from-blue-900/40 to-purple-900/40 rounded-lg p-8 mb-16">
                <div className="md:flex items-center justify-between">
                  <div className="mb-6 md:mb-0 md:mr-8">
                    <h2 className="text-2xl font-bold mb-2">
                      Ready to light up your display?
                    </h2>
                    <p className="text-gray-300">
                      Create an account to download sequences and start building your holiday light show today.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Button className="bg-blue-600 hover:bg-blue-700 transition-colors text-white px-6 py-3 rounded-md font-medium">
                      Create Account
                    </Button>
                    <Button variant="outline" className="bg-gray-700 hover:bg-gray-600 border-gray-600 transition-colors text-white px-6 py-3 rounded-md font-medium">
                      Learn More
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}

